import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendUp, TrendDown, Plus, Trash, PencilSimple, DownloadSimple, Ticket, Wallet, Clock, UserList, ArrowsLeftRight, CurrencyEur } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import { MonthField } from '@/components/ui/date-field';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';
import { fmtMonth } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const PROGRAMS = ['Super Barriguinhas', 'Super Mamãs', 'Super Pré Mamãs', 'Super Bebés', 'Geral'];
const eur = (v) => `${(Number(v) || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

function StatCard({ icon: Icon, label, value, sub, change }) {
  return (
    <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={15} weight="duotone" className="text-brand-gold-hover" />
        <p className="text-[11px] text-brand-muted uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-heading font-bold text-brand-text">{value}</p>
      {sub && <p className="text-[11px] text-brand-muted mt-0.5">{sub}</p>}
      {change !== undefined && change !== null && (
        <div className="flex items-center gap-1 mt-1">
          {change >= 0 ? <TrendUp size={12} className="text-brand-green" /> : <TrendDown size={12} className="text-red-500" />}
          <span className={`text-[11px] font-medium ${change >= 0 ? 'text-brand-green' : 'text-red-500'}`}>{change >= 0 ? '+' : ''}{change}%</span>
          <span className="text-[10px] text-brand-muted">vs mês anterior</span>
        </div>
      )}
    </div>
  );
}

export default function MetricasAvancadasPage() {
  useRequireAdmin();
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const isViewer = user?.role === 'viewer';
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [program, setProgram] = useState('');
  const [nutriId, setNutriId] = useState('');
  const [nutris, setNutris] = useState([]);
  const [patientTags, setPatientTags] = useState([]);
  const [tag, setTag] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSaleForm, setShowSaleForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [saleForm, setSaleForm] = useState({ date: new Date().toISOString().split('T')[0], description: '', amount: '', category: '' });
  const [includeHistorical, setIncludeHistorical] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ month });
      if (program) params.append('program', program);
      if (nutriId) params.append('nutri_id', nutriId);
      if (tag) params.append('tag', tag);
      if (includeHistorical) params.append('include_historical', 'true');
      const { data } = await axios.get(`${API}/api/ceo/advanced-metrics?${params.toString()}`);
      setData(data);
    } catch { toast.error('Erro ao carregar métricas'); } finally { setLoading(false); }
  }, [month, program, nutriId, tag, includeHistorical]);
  useEffect(() => { fetch(); }, [fetch]);
  useEffect(() => {
    (async () => { try { const { data } = await axios.get(`${API}/api/staff`); setNutris(data); } catch {} })();
    (async () => { try { const { data } = await axios.get(`${API}/api/patient-tags`); setPatientTags(data?.tags || []); } catch {} })();
  }, []);

  const exportCsv = () => {
    const params = new URLSearchParams({ month });
    if (program) params.append('program', program);
    if (nutriId) params.append('nutri_id', nutriId);
    if (tag) params.append('tag', tag);
    window.open(`${API}/api/ceo/advanced-metrics/csv?${params.toString()}`, '_blank');
  };

  const openNewSale = () => { setEditingSale(null); setSaleForm({ date: `${month}-01`, description: '', amount: '', category: '' }); setShowSaleForm(true); };
  const openEditSale = (s) => { setEditingSale(s); setSaleForm({ date: s.date, description: s.description, amount: String(s.amount), category: s.category || '' }); setShowSaleForm(true); };
  const saveSale = async () => {
    if (!saleForm.date || !saleForm.description.trim()) { toast.error('Data e descrição obrigatórias'); return; }
    try {
      if (editingSale) await axios.put(`${API}/api/other-services-sales/${editingSale.id}`, { ...saleForm, amount: parseFloat(saleForm.amount) || 0 });
      else await axios.post(`${API}/api/other-services-sales`, { ...saleForm, amount: parseFloat(saleForm.amount) || 0 });
      toast.success('Venda guardada'); setShowSaleForm(false); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };
  const deleteSale = async (id) => {
    const s = data?.other_services?.list?.find(x => x.id === id);
    const ok = await confirmDelete({ description: `Vai apagar a venda "${s?.description || ''}" (${s?.amount || 0}€).` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/other-services-sales/${id}`); fetch(); } catch { toast.error('Erro'); }
  };

  if (loading && !data) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;
  if (!data) return null;

  const tm = data.ticket_medio, cpc = data.cost_per_client, ad = data.avg_active_duration_months, wl = data.waitlist_conversion, fr = data.followup_ratio, rev = data.revenue;
  const maxNutri = Math.max(1, ...data.consults_by_nutri.map(n => n.month_total));

  return (
    <div className="fade-in" data-testid="metricas-avancadas-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">Métricas Avançadas</h2>
          <p className="text-sm text-brand-muted">Indicadores de negócio sobre a base financeira consolidada</p>
        </div>
        <button onClick={exportCsv} className="flex items-center gap-1.5 border border-brand-border text-brand-green rounded-full px-4 py-2 text-sm hover:bg-brand-green/5" data-testid="export-csv-btn"><DownloadSimple size={15} /> Exportar CSV</button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <MonthField value={month} onChange={e => setMonth(e.target.value)} className="border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="month-filter" />
        <select value={program} onChange={e => setProgram(e.target.value)} className="border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="program-filter">
          <option value="">Todos os programas</option>
          {PROGRAMS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={nutriId} onChange={e => setNutriId(e.target.value)} className="border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="nutri-filter">
          <option value="">Todos os profissionais</option>
          {nutris.map(n => <option key={n.user_id} value={n.user_id}>{n.name}</option>)}
        </select>
        <select value={tag} onChange={e => setTag(e.target.value)} className="border border-brand-border rounded-xl px-3 py-2 text-sm bg-white" data-testid="tag-filter">
          <option value="">Todas as tags</option>
          {patientTags.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        {(program || nutriId || tag) && <button onClick={() => { setProgram(''); setNutriId(''); setTag(''); }} className="text-xs text-brand-green hover:underline">Limpar filtros</button>}
        <button onClick={() => setIncludeHistorical(v => !v)} className={`ml-auto text-xs px-3 py-2 rounded-xl font-medium border ${includeHistorical ? 'bg-brand-green text-white border-brand-green' : 'bg-white text-brand-muted border-brand-border'}`} data-testid="toggle-historical-metrics">
          {includeHistorical ? '✓ ' : ''}Incluir receita histórica
        </button>
      </div>
      {includeHistorical && <p className="text-[11px] text-brand-placeholder -mt-3 mb-4">A receita histórica está incluída nos totais de receita. Renovação, LTV e churn por nutricionista nunca incluem históricos.</p>}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        <StatCard icon={Ticket} label="Ticket médio" value={eur(tm.overall.current)} sub={`${data.consultations_count} consultas`} change={tm.overall.change} />
        {cpc.current !== null && cpc.current !== undefined && (
          <StatCard icon={Wallet} label="Custo por cliente" value={eur(cpc.current)} sub={`${eur(cpc.total_expenses)} despesas · ${cpc.active_clients} ativos`} change={cpc.change} />
        )}
        <StatCard icon={Clock} label="Tempo médio activa" value={`${ad.overall} meses`} sub="entre 1.ª e última consulta" />
        <StatCard icon={UserList} label="Conversão Lista Espera" value={`${wl.rate}%`} sub={`${wl.converted} de ${wl.total} entradas`} />
        <StatCard icon={ArrowsLeftRight} label="Seguimento vs 1.ª" value={fr.ratio ? `${fr.ratio}×` : '-'} sub={`${fr.followup} seguimento · ${fr.first} primeiras`} />
        <StatCard icon={CurrencyEur} label="Receita total" value={eur(rev.total)} sub={`Consultas ${eur(rev.consultations)} + Outros ${eur(rev.other_services)}`} change={rev.change} />
      </div>

      {/* Breakdown por programa */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="ticket-by-program">
          <h3 className="font-heading text-sm text-brand-text mb-3">Ticket médio por programa</h3>
          {Object.keys(tm.by_program).length === 0 ? <p className="text-xs text-brand-muted">Sem dados.</p> : (
            <ul className="space-y-2">{Object.entries(tm.by_program).map(([k, v]) => (
              <li key={k} className="flex justify-between text-sm"><span className="text-brand-muted">{k}</span><span className="font-medium text-brand-text">{eur(v)}</span></li>
            ))}</ul>
          )}
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="duration-by-program">
          <h3 className="font-heading text-sm text-brand-text mb-3">Tempo médio activa por programa</h3>
          {Object.keys(ad.by_program).length === 0 ? <p className="text-xs text-brand-muted">Sem dados.</p> : (
            <ul className="space-y-2">{Object.entries(ad.by_program).map(([k, v]) => (
              <li key={k} className="flex justify-between text-sm"><span className="text-brand-muted">{k}</span><span className="font-medium text-brand-text">{v} meses</span></li>
            ))}</ul>
          )}
        </div>
        <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="ratio-by-program">
          <h3 className="font-heading text-sm text-brand-text mb-3">Seguimento vs 1.ª por programa</h3>
          {Object.keys(fr.by_program).length === 0 ? <p className="text-xs text-brand-muted">Sem dados.</p> : (
            <ul className="space-y-2">{Object.entries(fr.by_program).map(([k, v]) => (
              <li key={k} className="flex justify-between text-sm"><span className="text-brand-muted">{k}</span><span className="font-medium text-brand-text">{v.ratio ? `${v.ratio}×` : '-'} <span className="text-[10px] text-brand-muted">({v.followup}/{v.first})</span></span></li>
            ))}</ul>
          )}
        </div>
      </div>

      {/* Consultas por nutri (Ana vs Filipa) */}
      <div className="bg-white border border-brand-border rounded-2xl p-4 mb-6" data-testid="consults-by-nutri">
        <h3 className="font-heading text-sm text-brand-text mb-3">Consultas por profissional (mês e média semanal)</h3>
        <div className="space-y-3">
          {data.consults_by_nutri.map(n => (
            <div key={n.nutri_id} className="flex items-center gap-3" data-testid={`nutri-bar-${n.nutri_id}`}>
              <div className="w-32 shrink-0 text-sm text-brand-text truncate">{n.name}</div>
              <div className="flex-1 h-5 bg-brand-bg rounded-full overflow-hidden">
                <div className="h-full bg-brand-green rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(8, (n.month_total / maxNutri) * 100)}%` }}>
                  <span className="text-[10px] font-semibold text-white">{n.month_total}</span>
                </div>
              </div>
              <div className="w-24 shrink-0 text-right text-[11px] text-brand-muted">{n.weekly_avg}/semana</div>
            </div>
          ))}
        </div>
      </div>

      {/* Outros serviços (cursos, ebooks) */}
      <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="other-services-section">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading text-sm text-brand-text">Outros serviços (cursos, ebooks)</h3>
            <p className="text-[11px] text-brand-muted">Receita fora do modelo de consultas · Total {eur(data.other_services.total)}</p>
          </div>
          {!isViewer && <button onClick={openNewSale} className="flex items-center gap-1.5 bg-brand-green text-white rounded-full px-3 py-1.5 text-xs font-medium" data-testid="add-sale-btn"><Plus size={13} /> Registar venda</button>}
        </div>
        {data.other_services.list.length === 0 ? (
          <p className="text-xs text-brand-muted py-2">Sem vendas registadas neste mês.</p>
        ) : (
          <ul className="space-y-1.5">
            {data.other_services.list.map(s => (
              <li key={s.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-brand-bg/40" data-testid={`sale-${s.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-brand-text truncate">{s.description}</p>
                  <p className="text-[10px] text-brand-muted">{fmtDatePt(s.date)}{s.category ? ` · ${s.category}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm font-medium text-brand-text">{eur(s.amount)}</span>
                  {!isViewer && <button onClick={() => openEditSale(s)} className="p-1 text-brand-placeholder hover:text-brand-green" data-testid={`edit-sale-${s.id}`}><PencilSimple size={13} /></button>}
                  {!isViewer && <button onClick={() => deleteSale(s.id)} className="p-1 text-brand-placeholder hover:text-red-500" data-testid={`delete-sale-${s.id}`}><Trash size={13} /></button>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ════ FASE 2B ════ */}
      {data.occupancy && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="section-occupancy">
          <h3 className="font-heading text-sm text-brand-text mb-1">Taxa de ocupação</h3>
          <p className="text-[11px] text-brand-muted mb-3">Consultas realizadas vs vagas configuradas · {data.occupancy.total.rate}% global ({data.occupancy.total.done}/{data.occupancy.total.slots} vagas) · {data.occupancy.total.unfilled} vagas por preencher (<span className="text-red-500 font-medium">{eur(data.occupancy.total.lost_revenue)} receita potencial perdida</span>)</p>
          <div className="space-y-2">
            {data.occupancy.by_nutri.map(r => (
              <div key={r.nutri_id} className="flex items-center gap-3" data-testid={`occupancy-${r.nutri_id}`}>
                <span className="w-28 shrink-0 text-sm text-brand-text truncate">{r.name}</span>
                <div className="flex-1 h-5 bg-brand-bg rounded-full overflow-hidden"><div className="h-full bg-brand-green rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.min(100, r.rate)}%` }}><span className="text-[10px] font-semibold text-white">{r.rate}%</span></div></div>
                <span className="w-44 shrink-0 text-right text-[11px] text-brand-muted">{r.done}/{r.slots} · {r.unfilled} livres{r.days_worked ? ` · ${r.days_worked} dias trabalhados` : ''}{r.days_worked ? ` · ${r.consults_per_day}/dia` : ''}</span>
              </div>
            ))}
            {data.occupancy.by_nutri.every(r => r.slots === 0) && <p className="text-xs text-brand-muted">Configura as vagas em Definições → Disponibilidade.</p>}
          </div>
        </div>
      )}

      {data.consults_per_day && data.consults_per_day.current && data.consults_per_day.current.length > 0 && (() => {
        const cpd = data.consults_per_day;
        const PAL = ['#657161', '#C9A24B', '#8FA68E', '#B5651D'];
        const nutriColor = (id) => PAL[cpd.nutris.findIndex(n => n.id === id) % PAL.length];
        const fmtMonth = (m) => { const [y, mo] = m.split('-'); return `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(mo, 10) - 1]} ${y.slice(2)}`; };
        const maxR = Math.max(1, ...cpd.history.flatMap(h => cpd.nutris.map(n => h[n.id] || 0)));
        const isComparative = user?.role === 'admin' && cpd.current.length > 1;
        return (
          <div className="bg-white border border-brand-border rounded-2xl p-4 mt-6" data-testid="section-consults-per-day">
            <h3 className="font-heading text-sm text-brand-text mb-1">Consultas por dia trabalhado</h3>
            <p className="text-[11px] text-brand-muted mb-3">Consultas realizadas ÷ dias trabalhados · mês atual{isComparative ? ' · comparativo' : ''}</p>
            <div className={`grid gap-2 ${cpd.current.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {cpd.current.map(c => (
                <div key={c.nutri_id} className="border border-brand-border rounded-xl p-3" data-testid={`cpd-current-${c.nutri_id}`}>
                  <div className="flex items-center gap-1.5 mb-0.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: nutriColor(c.nutri_id) }} /><span className="text-[11px] text-brand-muted truncate">{c.name}</span></div>
                  <p className="text-2xl font-heading font-bold text-brand-text">{c.ratio}<span className="text-xs font-normal text-brand-muted">/dia</span></p>
                  <p className="text-[10px] text-brand-placeholder">{c.consults} consultas · {c.days_worked} dias</p>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-brand-muted mt-4 mb-2">Evolução (últimos 6 meses)</p>
            <div className="flex items-end justify-between gap-2 h-32">
              {cpd.history.map(h => (
                <div key={h.month} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`cpd-bar-${h.month}`}>
                  <div className="w-full flex items-end justify-center gap-0.5 h-full">
                    {cpd.nutris.map(n => (
                      <div key={n.id} className="flex-1 rounded-t" style={{ height: `${Math.max(((h[n.id] || 0) / maxR) * 100, 2)}%`, background: nutriColor(n.id) }} title={`${n.name} · ${fmtMonth(h.month)}: ${h[n.id] || 0}/dia`} />
                    ))}
                  </div>
                  <span className="text-[9px] text-brand-muted mt-1">{fmtMonth(h.month)}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center flex-wrap gap-3 mt-2">
              {cpd.nutris.map(n => <span key={n.id} className="flex items-center gap-1 text-[9px] text-brand-muted"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: nutriColor(n.id) }} /> {n.name}</span>)}
            </div>
            {Array.isArray(data.cost_per_client_by_nutri) && data.cost_per_client_by_nutri.length > 0 && (
              <div className="mt-4 pt-3 border-t border-brand-border">
                <p className="text-[10px] text-brand-muted mb-2">Custo por cliente (despesas gerais rateadas por clientes ativos + diretas)</p>
                <div className="space-y-1.5">
                  {data.cost_per_client_by_nutri.map(c => (
                    <div key={c.nutri_id} className="flex items-center justify-between text-xs" data-testid={`cpc-nutri-${c.nutri_id}`}>
                      <span className="text-brand-text flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm inline-block" style={{ background: nutriColor(c.nutri_id) }} />{c.name}</span>
                      <span className="text-brand-muted">{eur(c.cost_per_client)}/cliente <span className="text-[9px] text-brand-placeholder">({c.active_clients} ativos · {eur(c.direct_expenses)} diretas + {eur(c.general_share)} rateado)</span></span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}


      {data.no_show && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4" data-testid="section-noshow">
          <h3 className="font-heading text-sm text-brand-text mb-1">Taxa de no-show</h3>
          <p className="text-[11px] text-brand-muted mb-3">Faltas vs consultas agendadas · {data.no_show.total.rate}% global ({data.no_show.total.no_show}/{data.no_show.total.scheduled}) · <span className="text-red-500 font-medium">{eur(data.no_show.total.lost_revenue)} perdidos</span></p>
          <ul className="space-y-1.5">
            {data.no_show.by_nutri.map(r => (
              <li key={r.nutri_id} className="flex justify-between text-sm" data-testid={`noshow-${r.nutri_id}`}><span className="text-brand-muted">{r.name}</span><span className="text-brand-text">{r.rate}% <span className="text-[10px] text-brand-muted">({r.no_show}/{r.scheduled} · {eur(r.lost_revenue)})</span></span></li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        {data.ltv_by_program && (
          <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="section-ltv">
            <h3 className="font-heading text-sm text-brand-text mb-1">LTV por programa</h3>
            <p className="text-[11px] text-brand-muted mb-3">Ticket médio × permanência média (clientes em acompanhamento activo)</p>
            <ul className="space-y-2">
              {Object.entries(data.ltv_by_program).map(([k, v]) => (
                <li key={k} className="flex justify-between text-sm"><span className="text-brand-muted">{k}</span><span className="font-medium text-brand-text">{eur(v.ltv)} <span className="text-[10px] text-brand-muted">({eur(v.ticket)} × {v.avg_months}m)</span></span></li>
              ))}
            </ul>
          </div>
        )}
        {data.renewal_by_program && (
          <div className="bg-white border border-brand-border rounded-2xl p-4" data-testid="section-renewal">
            <h3 className="font-heading text-sm text-brand-text mb-1">Taxa de renovação por programa</h3>
            <p className="text-[11px] text-brand-muted mb-3">Clientes que iniciaram 2.º ciclo / total que terminou</p>
            <ul className="space-y-2">
              {Object.entries(data.renewal_by_program).map(([k, v]) => (
                <li key={k} className="flex justify-between text-sm"><span className="text-brand-muted">{k}</span><span className="font-medium text-brand-text">{v.rate}% <span className="text-[10px] text-brand-muted">({v.renewed}/{v.terminated})</span></span></li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {data.churn && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4" data-testid="section-churn">
          <h3 className="font-heading text-sm text-brand-text mb-1">Churn por programa e fase</h3>
          <p className="text-[11px] text-brand-muted mb-3">Clientes que passaram a inactivas no período / activas (base: em acompanhamento activo)</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-brand-muted mb-1">Por programa</p>
              <ul className="space-y-1.5">{Object.entries(data.churn.by_program).map(([k, v]) => (
                <li key={k} className="flex justify-between text-sm"><span className="text-brand-muted">{k}</span><span className={`font-medium ${v.rate > 10 ? 'text-red-500' : 'text-brand-text'}`}>{v.rate}% <span className="text-[10px] text-brand-muted">({v.churned}/{v.active + v.churned})</span></span></li>
              ))}</ul>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-brand-muted mb-1">Por fase clínica</p>
              {Object.keys(data.churn.by_phase).length === 0 ? <p className="text-xs text-brand-muted">Sem dados de fase.</p> : (
                <ul className="space-y-1.5">{Object.entries(data.churn.by_phase).map(([k, v]) => (
                  <li key={k} className="flex justify-between text-sm"><span className="text-brand-muted">{k}</span><span className={`font-medium ${v.rate > 10 ? 'text-red-500' : 'text-brand-text'}`}>{v.rate}% <span className="text-[10px] text-brand-muted">({v.churned}/{v.active + v.churned})</span></span></li>
                ))}</ul>
              )}
            </div>
          </div>
        </div>
      )}

      {Array.isArray(data.metrics_by_nutri) && data.metrics_by_nutri.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4" data-testid="section-metrics-by-nutri">
          <h3 className="font-heading text-sm text-brand-text mb-1">Por nutricionista</h3>
          <p className="text-[11px] text-brand-muted mb-3">Renovação, LTV e churn por profissional (agregado por ID canónico)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="metrics-by-nutri-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-brand-muted border-b border-brand-border/60">
                  <th className="py-2 pr-3 font-medium">Profissional</th>
                  <th className="py-2 px-3 font-medium text-right">Taxa de renovação</th>
                  <th className="py-2 px-3 font-medium text-right">LTV</th>
                  <th className="py-2 pl-3 font-medium text-right">Churn</th>
                </tr>
              </thead>
              <tbody>
                {data.metrics_by_nutri.map(n => (
                  <tr key={n.nutri_id} className="border-b border-brand-border/30 last:border-0" data-testid={`metrics-nutri-${n.nutri_id}`}>
                    <td className="py-2.5 pr-3 text-brand-text">{n.name}</td>
                    <td className="py-2.5 px-3 text-right font-medium text-brand-text">{n.renewal.rate}% <span className="text-[10px] text-brand-muted">({n.renewal.renewed}/{n.renewal.terminated})</span></td>
                    <td className="py-2.5 px-3 text-right font-medium text-brand-text">{eur(n.ltv)} <span className="text-[10px] text-brand-muted">({n.ltv_clients} cli.)</span></td>
                    <td className={`py-2.5 pl-3 text-right font-medium ${n.churn.rate > 10 ? 'text-red-500' : 'text-brand-text'}`}>{n.churn.rate}% <span className="text-[10px] text-brand-muted">({n.churn.churned}/{n.churn.active + n.churn.churned})</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.new_patients_by_origin && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4" data-testid="section-origins">
          <h3 className="font-heading text-sm text-brand-text mb-1">Novos pacientes por origem</h3>
          <p className="text-[11px] text-brand-muted mb-3">1.as consultas no período, por origem da 1.ª consulta</p>
          {Object.keys(data.new_patients_by_origin).length === 0 ? <p className="text-xs text-brand-muted">Sem 1.as consultas neste período.</p> : (() => {
            const entries = Object.entries(data.new_patients_by_origin).sort((a, b) => b[1] - a[1]);
            const max = Math.max(1, ...entries.map(e => e[1]));
            const total = entries.reduce((s, e) => s + e[1], 0);
            return (
              <div className="space-y-2">
                {entries.map(([origin, count]) => (
                  <div key={origin} className="flex items-center gap-3" data-testid={`origin-bar-${origin}`}>
                    <span className="w-28 shrink-0 text-sm text-brand-text truncate">{origin}</span>
                    <div className="flex-1 h-5 bg-brand-bg rounded-full overflow-hidden">
                      <div className="h-full bg-brand-green rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(8, (count / max) * 100)}%` }}>
                        <span className="text-[10px] font-semibold text-white">{count}</span>
                      </div>
                    </div>
                    <span className="w-12 shrink-0 text-right text-[11px] text-brand-muted">{total ? Math.round((count / total) * 100) : 0}%</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {Array.isArray(data.ltv_by_origin) && data.ltv_by_origin.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4" data-testid="section-ltv-by-origin">
          <h3 className="font-heading text-sm text-brand-text mb-1">LTV por canal de aquisição</h3>
          <p className="text-[11px] text-brand-muted mb-3">Valor médio gerado por paciente, segundo a origem da 1.ª consulta</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ltv-by-origin-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-brand-muted border-b border-brand-border/60">
                  <th className="py-2 pr-3 font-medium">Origem</th>
                  <th className="py-2 px-3 font-medium text-right">Nº pacientes</th>
                  <th className="py-2 pl-3 font-medium text-right">LTV médio</th>
                </tr>
              </thead>
              <tbody>
                {data.ltv_by_origin.map(o => (
                  <tr key={o.origin} className="border-b border-brand-border/30 last:border-0" data-testid={`ltv-origin-${o.origin}`}>
                    <td className="py-2.5 pr-3 text-brand-text">{o.origin}</td>
                    <td className="py-2.5 px-3 text-right text-brand-muted">{o.patients}</td>
                    <td className="py-2.5 pl-3 text-right font-medium text-brand-text">{eur(o.ltv)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data.revenue_forecast && (
        <div className="bg-white border border-brand-gold/30 rounded-2xl p-4 mt-4" data-testid="section-forecast">
          <h3 className="font-heading text-sm text-brand-text mb-1">Previsão de receita · {fmtMonth(data.revenue_forecast.next_month)}</h3>
          {data.revenue_forecast.accuracy && (() => {
            const a = data.revenue_forecast.accuracy;
            const good = a.accuracy_pct >= 85, mid = a.accuracy_pct >= 70;
            const cls = good ? 'bg-green-50 text-green-700 border-green-200' : mid ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200';
            return (
              <div className={`inline-flex items-center gap-2 text-[11px] border rounded-full px-3 py-1 mb-1 ${cls}`} data-testid="forecast-accuracy">
                <span className="font-semibold">{a.accuracy_pct}% de precisão</span>
                <span className="opacity-80">({fmtMonth(a.month)}: previsto {eur(a.predicted)} · real {eur(a.actual)})</span>
              </div>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            <div className="bg-brand-mint/10 rounded-xl p-3"><p className="text-[10px] text-brand-green uppercase tracking-wider">Garantido</p><p className="text-xl font-heading font-bold text-brand-text">{eur(data.revenue_forecast.guaranteed.amount)}</p><p className="text-[10px] text-brand-muted">{data.revenue_forecast.guaranteed.appointments} consultas já marcadas</p></div>
            <div className="bg-brand-gold/10 rounded-xl p-3"><p className="text-[10px] text-brand-gold-hover uppercase tracking-wider">Estimativa</p><p className="text-xl font-heading font-bold text-brand-text">{eur(data.revenue_forecast.estimate.amount)}</p><p className="text-[10px] text-brand-muted">{data.revenue_forecast.estimate.clients} activas sem marcação</p></div>
            <div className="bg-brand-bg rounded-xl p-3"><p className="text-[10px] text-brand-muted uppercase tracking-wider">Total previsto</p><p className="text-xl font-heading font-bold text-brand-green">{eur(data.revenue_forecast.total)}</p><p className="text-[10px] text-brand-muted">garantido + estimativa</p></div>
          </div>
          {/* Tendência: receita real dos últimos 6 meses + previsão */}
          {Array.isArray(data.revenue_forecast.history) && data.revenue_forecast.history.length > 0 && (() => {
            const hist = data.revenue_forecast.history.map(h => ({ ...h, predicted: false }));
            const series = [...hist, { month: data.revenue_forecast.next_month, revenue: data.revenue_forecast.total, predicted: true }];
            const max = Math.max(...series.map(s => s.revenue), 1);
            const fmtMonth = (m) => { const [y, mo] = m.split('-'); return `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(mo, 10) - 1]} ${y.slice(2)}`; };
            return (
              <div className="mt-4 border-t border-brand-border/40 pt-4" data-testid="forecast-trend">
                <p className="text-[10px] text-brand-muted uppercase tracking-wider mb-3">Receita real (6 meses) vs. previsão</p>
                <div className="flex items-end justify-between gap-2 h-36">
                  {series.map((s) => (
                    <div key={s.month} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`trend-bar-${s.month}`}>
                      <span className="text-[9px] font-medium text-brand-text mb-1">{eur(s.revenue)}</span>
                      <div
                        className={`w-full rounded-t-lg transition-all ${s.predicted ? 'bg-brand-gold/60 border border-dashed border-brand-gold-hover' : 'bg-brand-green/80'}`}
                        style={{ height: `${Math.max((s.revenue / max) * 100, 2)}%` }}
                        title={`${fmtMonth(s.month)}: ${eur(s.revenue)}`}
                      />
                      <span className={`text-[9px] mt-1 ${s.predicted ? 'text-brand-gold-hover font-medium' : 'text-brand-muted'}`}>{fmtMonth(s.month)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <span className="flex items-center gap-1 text-[9px] text-brand-muted"><span className="w-2.5 h-2.5 rounded-sm bg-brand-green/80 inline-block" /> Receita real</span>
                  <span className="flex items-center gap-1 text-[9px] text-brand-muted"><span className="w-2.5 h-2.5 rounded-sm bg-brand-gold/60 border border-dashed border-brand-gold-hover inline-block" /> Previsão</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ITEM 3 — Consultas presenciais vs online por mês */}
      {Array.isArray(data.modality_by_month) && data.modality_by_month.length > 0 && (
        <div className="bg-white border border-brand-border rounded-2xl p-4 mt-4" data-testid="section-modality">
          <h3 className="font-heading text-sm text-brand-text mb-1">Consultas presenciais vs online</h3>
          <p className="text-[10px] text-brand-muted mb-3">Últimos 6 meses{data.filters?.nutri_id ? ' · profissional filtrada' : ''}</p>
          {(() => {
            const series = data.modality_by_month;
            const max = Math.max(...series.map(s => (s.presencial || 0) + (s.online || 0)), 1);
            const fmtMonth = (m) => { const [y, mo] = m.split('-'); return `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(mo, 10) - 1]} ${y.slice(2)}`; };
            return (
              <>
                <div className="flex items-end justify-between gap-2 h-40">
                  {series.map(s => {
                    const total = (s.presencial || 0) + (s.online || 0);
                    return (
                      <div key={s.month} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`modality-bar-${s.month}`}>
                        <span className="text-[9px] font-medium text-brand-text mb-1">{total}</span>
                        <div className="w-full flex flex-col justify-end rounded-t-lg overflow-hidden" style={{ height: `${Math.max((total / max) * 100, 2)}%` }} title={`${fmtMonth(s.month)}: ${s.presencial || 0} presencial, ${s.online || 0} online`}>
                          <div className="w-full bg-brand-green/80" style={{ height: `${total ? ((s.presencial || 0) / total) * 100 : 0}%` }} />
                          <div className="w-full bg-brand-gold/70" style={{ height: `${total ? ((s.online || 0) / total) * 100 : 0}%` }} />
                        </div>
                        <span className="text-[9px] text-brand-muted mt-1">{fmtMonth(s.month)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center gap-4 mt-3">
                  <span className="flex items-center gap-1 text-[9px] text-brand-muted"><span className="w-2.5 h-2.5 rounded-sm bg-brand-green/80 inline-block" /> Presencial</span>
                  <span className="flex items-center gap-1 text-[9px] text-brand-muted"><span className="w-2.5 h-2.5 rounded-sm bg-brand-gold/70 inline-block" /> Online</span>
                </div>
              </>
            );
          })()}
        </div>
      )}
      <Dialog open={showSaleForm} onOpenChange={setShowSaleForm}>
        <DialogContent className="rounded-3xl border-brand-border" data-testid="sale-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">{editingSale ? 'Editar venda' : 'Registar venda'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs text-brand-muted mb-1 block">Data</Label><Input type="date" value={saleForm.date} onChange={e => setSaleForm({ ...saleForm, date: e.target.value })} className="rounded-xl border-brand-border" data-testid="sale-date" /></div>
            <div><Label className="text-xs text-brand-muted mb-1 block">Descrição</Label><Input value={saleForm.description} onChange={e => setSaleForm({ ...saleForm, description: e.target.value })} placeholder="Ex: Curso online, Ebook receitas" className="rounded-xl border-brand-border" data-testid="sale-description" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-brand-muted mb-1 block">Valor (€)</Label><Input type="number" step="0.01" value={saleForm.amount} onChange={e => setSaleForm({ ...saleForm, amount: e.target.value })} className="rounded-xl border-brand-border" data-testid="sale-amount" /></div>
              <div><Label className="text-xs text-brand-muted mb-1 block">Categoria (opcional)</Label><Input value={saleForm.category} onChange={e => setSaleForm({ ...saleForm, category: e.target.value })} placeholder="curso / ebook" className="rounded-xl border-brand-border" data-testid="sale-category" /></div>
            </div>
            <button onClick={saveSale} className="w-full bg-brand-green text-white rounded-full py-3 font-medium" data-testid="save-sale-btn">Guardar</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
